import jwtDecode from 'jwt-decode'
import merge from 'lodash/merge'
import { FetchLike, Middleware, WretcherOptions, WretcherResponse } from 'wretch'
import { env } from '../environment'
import { storeManager } from '../lib/storeManager'
import { AuthenticationActions } from '../redux/authentication/actions'
import { apiTokenSelector, credentialsSelector, isConnectedSelector } from '../redux/authentication/selectors'
import { AuthenticationApi } from './Authentication/AuthenticationApi'
import { SignInResult, SignUpResult } from './Authentication/types'

export const shouldRefreshFromToken = (token: string): boolean => {
  try {
    if (!token) {
      return true
    }
    const payload: { exp: number } = jwtDecode(token)
    const clockTimestamp = Math.floor(Date.now() / 1000)
    const clockTolerance = 5

    return payload && payload.exp < clockTimestamp - clockTolerance
  } catch (error) {
    return true
  }
}

let pendingAuthToken: Promise<string> | null = null

const getCredentials = async (): Promise<{ uuid: string; password: string }> => {
  const credentials = credentialsSelector(storeManager.store.getState())
  if (credentials) {
    return credentials
  }

  const { uuid, password } = (await AuthenticationApi.signUp(env().clientUuid)) as SignUpResult
  storeManager.store.dispatch(AuthenticationActions.storeUserCredentials(uuid, password))
  return { uuid, password }
}

const getNewAuthToken = async (): Promise<string> => {
  const authtoken = apiTokenSelector(storeManager.store.getState())
  const credentials = await getCredentials()

  const canRefresh = !!authtoken?.refreshToken && !shouldRefreshFromToken(authtoken.refreshToken)
  if (!canRefresh && isConnectedSelector(storeManager.store.getState())) {
    storeManager.store.dispatch(AuthenticationActions.storeIsConnected(false))
  }
  const newToken = (
    canRefresh ? await AuthenticationApi.refreshToken({ refreshToken: authtoken?.refreshToken || '' }) : await AuthenticationApi.signIn(credentials)
  ) as SignInResult

  storeManager.store.dispatch(AuthenticationActions.storeApiToken(newToken.accessToken, newToken.tokenType, newToken.refreshToken))
  return newToken.accessToken
}

const renewAuthToken = (): Promise<string> => {
  if (!pendingAuthToken) {
    pendingAuthToken = getNewAuthToken().finally(() => {
      pendingAuthToken = null
    })
  }
  return pendingAuthToken
}

export const getAuthToken = async () => {
  const authtoken = apiTokenSelector(storeManager.store.getState())
  if (!authtoken?.accessToken || shouldRefreshFromToken(authtoken.accessToken)) {
    return renewAuthToken()
  }
  return authtoken.accessToken
}

const getNewOpts = (options: WretcherOptions, tokenValue: string) =>
  merge(options, {
    headers: {
      Authorization: `Bearer ${tokenValue}`,
    },
  })

export const authMiddleware: Middleware =
  () =>
  (next: FetchLike) =>
  async (url: string, opts: WretcherOptions): Promise<WretcherResponse> => {
    const authToken = await getAuthToken()
    const newOpts = getNewOpts(opts, authToken)
    const response = await next(url, newOpts)

    return response
  }
